import { FC, useEffect, useState } from "react";
import MainButton from "@/components/ui/main-button";
import { DateTime } from "luxon";
import { useMe } from "@/hooks/use-me";
import { Header, Section, TextBlock } from "./account-elements";
import { getPause, humanReadableDate } from "./pause-utils";
import PauseSelector from "./pause-selector";
import CancelPauseButton from "./cancelPauseButton";

const PauseStatus: FC = () => {
  const user = useMe();
  const [pauseStart, setPauseStart] = useState<DateTime | null>(null);
  const [pauseEnd, setPauseEnd] = useState<DateTime | null>(null);
  const [pausedNow, setPausedNow] = useState<boolean | null>(false);
  const [currentYear] = useState<number>(DateTime.now().year);
  const [showPausePanel, setShowPausePanel] = useState<boolean>(false);

  useEffect(() => {
    if (!user) return;
    const pause = getPause(user);
    setPauseStart(pause.start);
    setPauseEnd(pause.end);
    setPausedNow(pause.pausedNow);
  }, [user]);

  return (
    <>
      {pauseStart && pauseEnd && !pausedNow && (
        <div className="grid gap-6 pt-2 col-span-3">
          <TextBlock>
            You have a pause scheduled from{" "}
            {humanReadableDate(pauseStart, currentYear !== pauseStart.year)},
            resuming on{" "}
            {humanReadableDate(pauseEnd, currentYear !== pauseEnd.year)}.
          </TextBlock>
          {pauseStart.toMillis() >
            DateTime.now().plus({ weeks: 1 }).toMillis() && (
            <div className="grid grid-cols-3">
              <CancelPauseButton
                handlePauseCancelled={() => setShowPausePanel(false)}
              />
            </div>
          )}
        </div>
      )}
      {pausedNow && (
        <div className="grid gap-6 pt-2 col-span-3">
          <TextBlock>
            <strong>
              Your plan is currently paused.
              <br />
              It will resume on{" "}
              {pauseEnd
                ? humanReadableDate(pauseEnd, currentYear !== pauseEnd.year)
                : "..."}
            </strong>
          </TextBlock>
        </div>
      )}
      {!pauseStart && (
        <div className="grid gap-6">
          <Section>
            {!showPausePanel && (
              <div className="grid grid-cols-3">
                <MainButton onClick={() => setShowPausePanel(true)}>
                  Schedule a pause
                </MainButton>
              </div>
            )}
            {showPausePanel && (
              <>
                <Header>Schedule a pause</Header>
                <TextBlock>
                  <p>
                    You can pause your plan whenever you like. Just remember to
                    provide us with a minimum of one week's notice, as we order
                    our fresh ingredients a week in advance.
                  </p>
                  <p className="mt-3">
                    If we've already taken your subscription payment for the
                    month, we'll credit your pause duration amount in the
                    following month.
                  </p>
                </TextBlock>
                <PauseSelector
                  handlePauseSelection={() => setShowPausePanel(false)}
                />
              </>
            )}
          </Section>
        </div>
      )}
    </>
  );
};

export default PauseStatus;
